import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { User, Building, DollarSign, ShieldCheck } from 'lucide-react';
import {
  KPICard, AgreementBlock, ComplianceBlock, ActivityBlock, NPSBlock, EverperformBlock,
  PerformanceRatingBlock,
  fmt, fmtCompact, fmtPct, Section,
} from './people/DeepDiveSections';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ManagerDeepDive = ({ initialManager, onConsumed }) => {
  const [managers, setManagers] = useState([]);
  const [selected, setSelected] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadList = useCallback(async () => {
    const res = await axios.get(`${API}/manager-deep-dive/list`);
    setManagers(res.data.managers);
  }, []);

  useEffect(() => { loadList(); }, [loadList]);

  // Single selection effect: prefer the pre-selection from the Performance
  // Ladder; otherwise fall back to alphabetical-first.  Runs whenever the
  // manager list or incoming pre-selection changes.
  useEffect(() => {
    if (!managers.length) return;
    if (initialManager && managers.some(m => m.manager === initialManager)) {
      setSelected(initialManager);
      if (onConsumed) onConsumed();
    } else if (!selected) {
      setSelected(managers[0].manager);
    }
  }, [managers, initialManager, selected, onConsumed]);

  useEffect(() => {
    if (!selected) return;
    setLoading(true);
    axios.get(`${API}/manager-deep-dive`, { params: { manager: selected } })
      .then(res => setData(res.data))
      .catch(err => console.error(err))
      .finally(() => setLoading(false));
  }, [selected]);

  return (
    <div data-testid="manager-deep-dive" className="space-y-6">
      {/* Picker */}
      <div className="bg-white border border-[#E5E5E5] rounded-[12px] p-5 shadow-[0_2px_10px_rgba(0,0,0,0.02)]">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="w-10 h-10 rounded-[10px] bg-[#171717] flex items-center justify-center">
            <User className="w-5 h-5 text-white" strokeWidth={1.5} />
          </div>
          <div className="flex-1 min-w-[250px]">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-[#A3A3A3]">OC Manager</p>
            <select
              data-testid="manager-select"
              value={selected}
              onChange={(e) => setSelected(e.target.value)}
              className="w-full mt-1 px-3 py-2 rounded-[8px] border border-[#E5E5E5] text-sm font-medium focus:outline-none focus:border-[#171717]"
            >
              {managers.map(m => (
                <option key={m.manager} value={m.manager}>
                  {m.manager} · {m.planCount} plans · {fmtCompact(m.totalRevenue)} {m.teamLeader ? ` · TL: ${m.teamLeader}` : ''}
                </option>
              ))}
            </select>
          </div>
          {data?.teamLeader && (
            <div className="text-right">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-[#A3A3A3]">Team Leader</p>
              <p className="text-sm font-semibold text-[#171717]">{data.teamLeader}</p>
            </div>
          )}
        </div>
      </div>

      {loading || !data ? (
        <div className="flex items-center justify-center h-96" data-testid="manager-deep-dive-loading">
          <div className="w-8 h-8 border-3 border-[#E5E5E5] border-t-[#171717] rounded-full animate-spin" />
        </div>
      ) : (
        <>
          {/* Performance Rating */}
          <PerformanceRatingBlock performanceRating={data.performanceRating} subjectLabel="Manager" />

          {/* Headline KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KPICard icon={Building} label="Plans Managed" value={data.planCount} testId="mdd-plan-count" />
            <KPICard icon={DollarSign} label="Total Revenue" value={fmtCompact(data.totalRevenue)} testId="mdd-revenue" />
            <KPICard icon={Building} label="Lots" value={data.totalLots.toLocaleString()} testId="mdd-lots" />
            <KPICard icon={ShieldCheck} label="Avg Compliance" value={data.avgComplianceScore != null ? `${data.avgComplianceScore}` : '—'} sublabel="of 100" testId="mdd-compliance-avg" />
          </div>

          <AgreementBlock agreement={data.agreement} />
          <ComplianceBlock compliance={data.compliance} />
          <ActivityBlock activity={data.activity} />
          <EverperformBlock everperform={data.everperform} />
          <NPSBlock nps={data.nps} />

          {/* Peer comparison */}
          {data.peerComparison?.length > 0 && (
            <Section title="Peer Comparison" icon={User} subtitle="Top 10 managers by revenue — see where you stand.">
              <div className="bg-white border border-[#E5E5E5] rounded-[10px] overflow-hidden">
                <table className="w-full" data-testid="mdd-peers">
                  <thead className="bg-[#FAFAFA] border-b border-[#E5E5E5]">
                    <tr>
                      <th className="text-left py-2.5 px-3 text-[10px] font-semibold uppercase tracking-wider text-[#737373]">Manager</th>
                      <th className="text-right py-2.5 px-3 text-[10px] font-semibold uppercase tracking-wider text-[#737373]">Plans</th>
                      <th className="text-right py-2.5 px-3 text-[10px] font-semibold uppercase tracking-wider text-[#737373]">Revenue</th>
                      <th className="text-right py-2.5 px-3 text-[10px] font-semibold uppercase tracking-wider text-[#737373]">Compliance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.peerComparison.map(p => (
                      <tr key={p.manager} className="border-b border-[#F0F0F0] last:border-0 hover:bg-[#FAFAFA]/40">
                        <td className="py-2 px-3 text-sm text-[#171717]">{p.manager}</td>
                        <td className="py-2 px-3 text-sm text-right text-[#525252]">{p.planCount}</td>
                        <td className="py-2 px-3 text-sm text-right font-semibold text-[#171717]">{fmtCompact(p.totalRevenue)}</td>
                        <td className="py-2 px-3 text-sm text-right text-[#525252]">{p.avgComplianceScore}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Section>
          )}

          {/* Plans table */}
          <Section title="Plans" icon={Building}>
            <div className="bg-white border border-[#E5E5E5] rounded-[10px] overflow-hidden">
              <table className="w-full" data-testid="mdd-plans">
                <thead className="bg-[#FAFAFA] border-b border-[#E5E5E5]">
                  <tr>
                    <th className="text-left py-2.5 px-3 text-[10px] font-semibold uppercase tracking-wider text-[#737373]">Plan</th>
                    <th className="text-left py-2.5 px-3 text-[10px] font-semibold uppercase tracking-wider text-[#737373]">Property</th>
                    <th className="text-right py-2.5 px-3 text-[10px] font-semibold uppercase tracking-wider text-[#737373]">Revenue</th>
                    <th className="text-right py-2.5 px-3 text-[10px] font-semibold uppercase tracking-wider text-[#737373]">Lots</th>
                    <th className="text-right py-2.5 px-3 text-[10px] font-semibold uppercase tracking-wider text-[#737373]">Compliance</th>
                    <th className="text-right py-2.5 px-3 text-[10px] font-semibold uppercase tracking-wider text-[#737373]">NPS</th>
                  </tr>
                </thead>
                <tbody>
                  {data.plans.map(p => (
                    <tr key={p.planNumber} className="border-b border-[#F0F0F0] last:border-0 hover:bg-[#FAFAFA]/40">
                      <td className="py-2 px-3 text-[12px] font-mono text-[#171717]">{p.planNumber}</td>
                      <td className="py-2 px-3 text-[12px] text-[#525252] truncate max-w-[300px]">{p.propertyName || '—'}</td>
                      <td className="py-2 px-3 text-[12px] text-right font-semibold text-[#171717]">{fmt(p.revenue)}</td>
                      <td className="py-2 px-3 text-[12px] text-right text-[#525252]">{p.occupiableLots}</td>
                      <td className="py-2 px-3 text-[12px] text-right text-[#525252]">{p.complianceScore ?? '—'}</td>
                      <td className="py-2 px-3 text-[12px] text-right text-[#525252]">{p.npsScore ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>
        </>
      )}
    </div>
  );
};

export default ManagerDeepDive;
